import {
  Tour,
  TourConditions,
  TourSpaceRole,
  TourTrigger,
  TourTriggerType,
} from '@pncl/common-models';
import { RE2JS } from 're2js';

export interface TourMatchContext {
  releaseVersion?: number;
  institutionId?: string;
  spaceRole?: TourSpaceRole;
  personas?: string[];
  isApiManaged?: boolean;
  isInIframe?: boolean;
}

type TourAudience = NonNullable<Tour['audience']>;

// Higher-priority tours should win when multiple tours match the same route/event.
export function sortTours(tours: Tour[]): Tour[] {
  return [...tours].sort((left, right) => (right.priority ?? 0) - (left.priority ?? 0));
}

// Matching only cares about the route itself, not transient query/hash state.
export function normalizeTourPath(path: string): string {
  return path.replace(/[?#].*$/, '');
}

// A tour is only actionable when its trigger carries the minimum data needed to fire.
export function hasTourTrigger(tour: Tour): boolean {
  if (tour.trigger?.type === TourTriggerType.EVENT) {
    return !!tour.trigger.eventName;
  }

  if (tour.trigger?.type === TourTriggerType.HOVER) {
    return !!tour.trigger.selector;
  }

  if (tour.trigger?.type === TourTriggerType.ROUTE_CHANGE) {
    return true;
  }

  return false;
}

// Build a fast lookup list of telemetry event names that can trigger tours.
export function getTriggerEventNames(tours: Tour[]): string[] {
  return tours.flatMap((tour) =>
    tour.trigger?.type === TourTriggerType.EVENT && tour.trigger.eventName
      ? [tour.trigger.eventName]
      : [],
  );
}

// Hover tours can optionally name an event that should cause their selector
// registration to be re-evaluated after the initial route-based attempt.
export function getHoverReevaluationEventNames(tours: Tour[]): string[] {
  return tours.flatMap((tour) =>
    tour.trigger?.type === TourTriggerType.HOVER && tour.trigger.eventName
      ? [tour.trigger.eventName]
      : [],
  );
}

// Plain hover tours participate during route evaluation. Event-named hover tours
// wait for their trigger event before registering DOM listeners.
// Event-triggered tours only match the named event.
export function matchesTourTrigger(trigger: TourTrigger, eventName?: string): boolean {
  if (trigger.type === TourTriggerType.EVENT) {
    return trigger.eventName === eventName;
  }

  if (trigger.type === TourTriggerType.HOVER) {
    return trigger.eventName ? trigger.eventName === eventName : !eventName;
  }

  if (trigger.type === TourTriggerType.ROUTE_CHANGE) {
    return !eventName;
  }

  return false;
}

// Conditions are route/runtime guards that sit alongside audience targeting.
export function matchesTourConditions(
  conditions: TourConditions | undefined,
  pagePath: string,
  ctx: TourMatchContext,
  onInvalidPagePathPattern?: (pattern: string) => void,
): boolean {
  if (!conditions) {
    return true;
  }

  if (!matchesPagePath(conditions.pagePath, pagePath, onInvalidPagePathPattern)) {
    return false;
  }

  if (!matchesReleaseVersion(conditions.releaseVersion, ctx.releaseVersion)) {
    return false;
  }

  return true;
}

// Audience checks are kept separate from route conditions so the service can build
// one shared runtime context and reuse these pure helpers in multiple places.
export function matchesTourAudience(
  audience: TourAudience | undefined,
  ctx: TourMatchContext,
): boolean {
  if (!audience) {
    return true;
  }

  if (audience.exclude?.apiManagedUsers && ctx.isApiManaged) {
    return false;
  }

  if (audience.exclude?.iframeUsers && ctx.isInIframe) {
    return false;
  }

  if (
    !matchesAudienceValues(
      audience.include?.institutionIds,
      audience.exclude?.institutionIds,
      ctx.institutionId,
    )
  ) {
    return false;
  }

  if (
    !matchesAudienceValues(audience.include?.personas, audience.exclude?.personas, ctx.personas)
  ) {
    return false;
  }

  if (
    !matchesAudienceValues(
      audience.include?.spaceRoles,
      audience.exclude?.spaceRoles,
      ctx.spaceRole,
    )
  ) {
    return false;
  }

  return true;
}

// Invalid admin-authored regex patterns should fail closed instead of breaking tour evaluation.
function matchesPagePath(
  pagePathPattern: string | undefined,
  currentPath: string,
  onInvalidPagePathPattern?: (pattern: string) => void,
): boolean {
  if (!pagePathPattern) {
    return true;
  }

  try {
    return RE2JS.compile(pagePathPattern).test(currentPath);
  } catch {
    onInvalidPagePathPattern?.(pagePathPattern);
    return false;
  }
}

// Release ranges are inclusive, and missing bounds mean "no lower/upper limit".
function matchesReleaseVersion(
  releaseVersionRange: TourConditions['releaseVersion'],
  currentReleaseVersion?: number,
): boolean {
  if (
    !releaseVersionRange ||
    ((releaseVersionRange.min === null || releaseVersionRange.min === undefined) &&
      (releaseVersionRange.max === null || releaseVersionRange.max === undefined))
  ) {
    return true;
  }

  if (currentReleaseVersion === null || currentReleaseVersion === undefined) {
    return false;
  }

  if (
    typeof releaseVersionRange.min === 'number' &&
    currentReleaseVersion < releaseVersionRange.min
  ) {
    return false;
  }

  if (
    typeof releaseVersionRange.max === 'number' &&
    currentReleaseVersion > releaseVersionRange.max
  ) {
    return false;
  }

  return true;
}

function matchesAudienceValues<T extends string>(
  includedValues: T[] | undefined,
  excludedValues: T[] | undefined,
  currentValue?: T | T[],
): boolean {
  const currentValues = normalizeAudienceValues(currentValue);

  if (includedValues?.length && !currentValues.some((value) => includedValues.includes(value))) {
    return false;
  }

  if (excludedValues?.length && currentValues.some((value) => excludedValues.includes(value))) {
    return false;
  }

  return true;
}

function normalizeAudienceValues<T>(value?: T | T[]): T[] {
  if (Array.isArray(value)) {
    return value;
  }

  return value === undefined ? [] : [value];
}
